import React, { useState, FormEvent, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { clearBulkVpaUpload, uploadVpaBulk } from "../../features/vpa/VpaSlice";
import { fetchTenantList } from "../../features/outlet/TenantSlice";
import { RootState } from "./../../store/store";
import { Loader2, AlertCircle, Download } from "lucide-react";
import PlaceholderImage from "./../../assets/images/placeholder_img.jpg";

const BulkVpaUpload: React.FC = () => {
  const dispatch = useAppDispatch();
  const { vpaLoading, vpaBulk, vpaError } = useAppSelector((state: RootState) => state.root.vpa);
  const { tenantList, tenantLoading, tenantError } = useAppSelector((state: RootState) => state.root.tenant);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [tenantId, setTenantId] = useState("");
  const [formErrors, setFormErrors] = useState({
    csvFileError: "",
    tenantError: "",
  });

  useEffect(() => {
    dispatch(fetchTenantList({
      limit: 1000,
      page: 1,
      searchTerm: ""
    }));
  }, [dispatch]);

  // Handling CSV file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setCsvFile(file || null);
  };

  // Clear form errors and bulk upload data when form changes
  const handleFormChange = () => {
    setFormErrors({
      csvFileError: "",
      tenantError: "",
    });
    dispatch(clearBulkVpaUpload());
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset error states
    setFormErrors({
      csvFileError: "",
      tenantError: "",
    });

    dispatch(clearBulkVpaUpload());

    // Validate tenant selection
    if (!tenantId) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        tenantError: "Please select a tenant.",
      }));
      return;
    }

    // Validate CSV file
    if (!csvFile) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        csvFileError: "Please upload a CSV file.",
      }));
      return;
    }

    dispatch(
      uploadVpaBulk({
        csvFile,
        tenantId,
      }),
    );
  };

  if (tenantLoading) {
    return (
      <div className="bg-white min-h-screen">
        <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
          <div className="flex items-center h-10">
            <h2 className="text-2xl font-bold text-gray-900">
              VPA Bulk Upload
            </h2>
          </div>
        </div>
        <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="bg-white min-h-screen">
        <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
          <div className="flex items-center h-10">
            <h2 className="text-2xl font-bold text-gray-900">
              VPA Bulk Upload
            </h2>
          </div>
        </div>
        <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{tenantError ? tenantError : "Something went wrong!"}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          <h2 className="text-2xl font-bold text-gray-900">
            VPA Bulk Upload
          </h2>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 mb-8">
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
          <form className="w-full" onChange={handleFormChange} onSubmit={handleSubmit}>
            {/* Form Fields Row */}
            <div className="flex justify-between md:flex-row gap-6 mb-6">
              {/* Tenant Selection */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tenant
                </label>
                <div className="relative">
                  <select
                    onChange={(e) => setTenantId(e.target.value)}
                    className={`block w-full px-3 py-2 border ${formErrors.tenantError ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                  >
                    <option value="">Select Tenant</option>
                    {tenantList?.tenants?.map((row: any) => (
                      <option key={row.tenantId} value={row.tenantId}>
                        {row.tenantName}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {formErrors.tenantError && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="mr-1" />
                      {formErrors.tenantError}
                    </div>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Upload
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
                  />
                  {formErrors.csvFileError && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="mr-1" />
                      {formErrors.csvFileError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="flex gap-4">
                <button
                  className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={vpaLoading}
                >
                  {vpaLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </div>
                  ) : (
                    "Upload"
                  )}
                </button>

                <a
                  href="https://saraluat.blr1.cdn.digitaloceanspaces.com/VPA_bulk_upload_template_underscored.csv"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </a>
              </div>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {vpaLoading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {vpaError && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{vpaError ? vpaError : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Results Table */}
        {vpaBulk?.vpa && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-start transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <div className="font-semibold text-lg mb-4">Upload Results</div>
            <div className="space-y-6">
              {vpaBulk.vpa.map((data: any, index: number) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex flex-row items-center">
                    {/* QR Code Section */}
                    <div className="w-20 h-20 p-2 bg-gray-50 flex items-center justify-center border-r border-gray-200 shrink-0">
                      <img 
                        src={data.qrImage || PlaceholderImage} 
                        alt="QR Code" 
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Details Section */}
                    <div className="flex-1 px-4 py-2">
                      <div className="flex flex-wrap items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">VPA ID:</span>
                          <span className="text-sm text-gray-900">{data.vpaId}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">VPA:</span>
                          <span className="text-sm text-gray-900">{data.vpa}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Tenant:</span>
                          <span className="text-sm text-gray-900">{data.tenantId}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Provider:</span>
                          <span className="text-sm text-gray-900">{data.providerName}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Status:</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${data.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                              data.status === 'INACTIVE' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'}`}
                          >
                            {data.status}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Mapping:</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${data.mappingStatus === 'MAPPED' ? 'bg-green-100 text-green-800' : 
                              data.mappingStatus === 'UNMAPPED' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'}`}
                          >
                            {data.mappingStatus}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkVpaUpload;
